import './AgentConversations.css'
import { useEffect, useState } from 'react'
import {
  agentConversationListSpec,
  type AgentConversationRow,
} from 'aevocam-rest-api-spec'
import { toastError } from '@/components/action/Toaster'
import { useApi } from '@/hooks/useApi'
import { foreshorten } from '@/utilities/stringHelpers'
import { cn } from '@/utilities/misc'
import { type DataGridState } from '@/components/dataGrid/createDataGridState'
import { DataPager } from '@/components/dataGrid/DataPager'

const PAGE_SIZE = 10

function formatUpdatedAt(value: Date | string | null | undefined) {
  if (value == null) return undefined
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return undefined
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function emptyListState(): DataGridState<AgentConversationRow> {
  return {
    rows: [],
    totalRows: 0,
    skip: 0,
    take: PAGE_SIZE,
  }
}

type Props = {
  agent: string
  /** UI page filter (eg 'articleList', 'articleEdit') */
  page?: string
  currentConversationId?: string
  onSelect: (conversationId: string) => void
}

export function AgentConversations({
  agent,
  page,
  currentConversationId,
  onSelect,
}: Props) {

  const api = useApi()
  const [listState, setListState] = useState<DataGridState<AgentConversationRow>>(emptyListState)

  //--------------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false

    async function loadPastConversations() {
      setListState(prev => ({
        ...prev,
        loading: !prev.refreshQuietly,
        loadingQuietly: !!prev.refreshQuietly,
      }))
      try {
        const res = await api.request(agentConversationListSpec, {
          body: {
            agent,
            page,
            skip: listState.skip,
            take: listState.take,
          },
        })
        if (cancelled) return
        if (res.status !== 200 || !res.data?.conversations) {
          toastError(res.error ?? 'Failed to load conversations')
          setListState(prev => ({
            ...prev,
            loading: false,
            loadingQuietly: false,
            refreshQuietly: false,
          }))
          return
        }
        setListState(prev => ({
          ...prev,
          rows: res.data?.conversations?.rows ?? [],
          totalRows: res.data?.conversations?.totalRows ?? 0,
          loading: false,
          loadingQuietly: false,
          refreshQuietly: false,
          lastRefreshedAt: Date.now(),
        }))
      } catch (error) {
        if (cancelled) return
        console.error(error)
        toastError('Failed to load conversations')
        setListState(prev => ({
          ...prev,
          loading: false,
          loadingQuietly: false,
          refreshQuietly: false,
        }))
      }
    }

    void loadPastConversations()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional fetch keys only
  }, [agent, page, listState.skip, listState.take, listState.refresh])

  //--------------------------------------------------------------------------------
  return (
    <div className='AgentConversations'>
      <div className='List'>
        {listState.rows.length === 0 && !listState.loading && (
          <div className='Empty'>No past conversations</div>
        )}
        {listState.rows.map((row, index) => {
          const id = row.id != null ? String(row.id) : undefined
          const isCurrent = id != null && id === currentConversationId
          return (
            <div key={id ?? index} className='Entry'>
              <button
                type='button'
                className={cn('Item', isCurrent && 'Current')}
                disabled={!id}
                onClick={() => id && onSelect(id)}
              >
                <span className='UpdatedAt'>
                  {formatUpdatedAt(row.updatedAt) ?? '—'}
                </span>
                <span className='Prompt'>
                  {foreshorten(row.initialPrompt ?? undefined, 200) || 'Untitled conversation'}
                </span>
              </button>
              {index < listState.rows.length - 1 && <hr />}
            </div>
          )
        })}
      </div>
      <DataPager
        state={listState}
        setState={setListState}
      />
    </div>
  )
}
